import { requestWithCookieAuth } from "api";
import { DIAGNOSTICS_API_BASE, PROJECT_IQ_API_BASE } from "config";

const fetchAllPaginatedItems = async (url, errorMessage) => {
  const pageSize = 1000;
  let allItems = [];
  let offset = 0;

  while (true) {
    const pageUrl = `${url}?limit=${pageSize}&offset=${offset}`;
    const response = await requestWithCookieAuth(pageUrl, {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    });

    if (response?.error) {
      throw new Error(response.message || errorMessage);
    }

    const pageItems = await response.json();
    allItems = allItems.concat(pageItems);

    if (pageItems.length < pageSize) {
      break;
    }

    offset += pageSize;
  }

  return allItems;
};

export const getProjectIqWorkspaces = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/mine/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch Project IQ workspaces",
    );
  }
  return await response.json();
};

export const createProjectIqWorkspace = async ({ workspace }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(workspace),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to create Project IQ workspace",
    );
  }
  return await response.json();
};

export const updateProjectIqWorkspace = async ({ id, workspace }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(workspace),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to update Project IQ workspace",
    );
  }
  return await response.json();
};

export const registerWorkspaceLogo = async (workspaceId, blobName) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/logo/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ blobName }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to register workspace logo");
  }
  return await response.json();
};

export const deleteWorkspaceLogo = async (workspaceId, blobUrl) => {
  const query = blobUrl ? `?blobUrl=${encodeURIComponent(blobUrl)}` : "";
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/logo/${query}`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete workspace logo");
  }
};

export const saveProjectPlan = async ({ id, projectPlan }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${id}/project-plan/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ projectPlan }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to update Project IQ workspace plan",
    );
  }
  return await response.json();
};

export const archiveProjectIqWorkspace = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${id}/archive/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to archive Project IQ workspace",
    );
  }
  return await response.json();
};

export const pinWorkspace = ({ workspaceId }) =>
  requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/pin/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );

export const unpinWorkspace = ({ workspaceId }) =>
  requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/unpin/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );

export const getProjectIqWorkspaceIndexingStatus = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/indexing-status/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch indexing status");
  }
  return await response.json();
};

export const getProjectIqWorkspaceDocuments = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch workspace documents");
  }
  return await response.json();
};

export const getSharedClientDocuments = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/shared/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch shared documents");
  }
  return await response.json();
};

/**
 * @param {{
 *   workspaceId: string,
 *   filename: string,
 *   originalFilename: string,
 *   source?: string,
 * }} params
 */
export const addProjectIqWorkspaceDocument = async ({
  workspaceId,
  filename,
  originalFilename,
  source = "file",
}) => {
  const body = {
    workspaceId,
    filename,
    originalFilename,
    source,
  };
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to add workspace document");
  }
  return await response.json();
};

export const getWorkspaceDocumentDownloadUrl = async ({
  workspaceId,
  filename,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/download-url/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filename }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to get workspace document download URL",
    );
  }
  return await response.json();
};

export const uploadQualityReviewDocument = async ({
  workspaceId,
  filename,
  fileData,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/quality-review/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filename, fileData }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to upload quality review document",
    );
  }
  return await response.json();
};

export const getQualityReviewDocument = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/quality-review/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch quality review document",
    );
  }
  return await response.json();
};

export const getQualityReviewDocumentDownloadUrl = async ({
  workspaceId,
  filename,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/quality-review/download-url/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filename }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to get quality review document download URL",
    );
  }
  return await response.json();
};

export const deleteQualityReviewDocument = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/quality-review/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to delete quality review document",
    );
  }
  return await response.json();
};

export const uploadRetrospectiveCaseStudy = async ({
  workspaceId,
  filename,
  fileData,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/retrospective/case-study/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filename, fileData }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to upload case study document");
  }
  return await response.json();
};

export const getRetrospectiveCaseStudy = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/retrospective/case-study/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch case study document");
  }
  return await response.json();
};

export const getRetrospectiveCaseStudyDownloadUrl = async ({
  workspaceId,
  filename,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/retrospective/case-study/download-url/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filename }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to get case study document download URL",
    );
  }
  return await response.json();
};

export const deleteRetrospectiveCaseStudy = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/retrospective/case-study/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete case study document");
  }
  return await response.json();
};

export const deleteProjectIqWorkspaceDocument = async ({
  workspaceId,
  filename,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        workspaceId,
        filename,
      }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete workspace document");
  }
  return await response.json();
};

export const setWorkspaceDocumentShareStatus = async ({
  workspaceId,
  filename,
  isSharedWithClient,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/share/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filename,
        isSharedWithClient,
      }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to update document share status",
    );
  }
  return await response.json();
};

export const setWorkspaceDocumentType = async ({
  workspaceId,
  filename,
  documentType,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/tag/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filename,
        documentType,
      }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update document type");
  }
  return await response.json();
};

export const getClientUsers = async (workspaceId) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/client-users/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch invited clients");
  }
  return await response.json();
};

export const inviteClientUser = async ({
  workspaceId,
  email,
  displayName,
  message,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/client-users/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, displayName, message }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to invite client");
  }
  return await response.json();
};

export const removeClientUser = async ({ workspaceId, clientUserId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/client-users/${clientUserId}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to remove client access");
  }
};

export const getProjectIqCapabilities = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/capabilities/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch Project IQ capabilities",
    );
  }
  return await response.json();
};

export const adminGetProjectIqCapabilities = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/capabilities/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch Project IQ capabilities",
    );
  }
  return await response.json();
};

export const adminCreateProjectIqCapability = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/capabilities/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to create Project IQ capability",
    );
  }
  return await response.json();
};

export const adminUpdateProjectIqCapability = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/capabilities/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to update Project IQ capability",
    );
  }
  return await response.json();
};

export const swapCapabilityOrder = async (movingId, targetId) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/capabilities/swap-order/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ moving_id: movingId, target_id: targetId }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to swap capability order");
  }
};

export const adminDeleteProjectIqCapability = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/capabilities/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to delete Project IQ capability",
    );
  }
  return response;
};

export const adminDeleteProjectIqWorkspace = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/workspaces/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to delete Project IQ workspace",
    );
  }
  return response;
};

export const adminGetProjectIqWorkspaces = async () => {
  return fetchAllPaginatedItems(
    `${PROJECT_IQ_API_BASE}/admin/workspaces/`,
    "Failed to fetch Project IQ workspaces",
  );
};

export const adminUpdateProjectIqWorkspace = async (id, workspace) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/workspaces/${id}/`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(workspace),
    },
  );
  if (!response.ok) throw new Error("Failed to update workspace");
  return response.json();
};

export const getDeliverables = async (workspaceId) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/deliverables/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to fetch deliverables");
  return response.json();
};

export const deleteDeliverable = async (workspaceId, deliverableId) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/deliverables/${deliverableId}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to delete deliverable");
};

export const updateDeliverable = async (workspaceId, deliverableId, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/deliverables/${deliverableId}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to update deliverable");
  return response.json();
};

export const savePeoplePage = async ({ id, people }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${id}/people/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ people }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update people page");
  }
  return await response.json();
};

export const saveProjectReview = async ({ id, projectReview }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${id}/project-review/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ projectReview }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update project review");
  }
  return await response.json();
};

export const getWorkspaceDiagnostics = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/diagnostics/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(
      response.message || "Failed to fetch workspace diagnostics",
    );
  return await response.json();
};

export const getDiagnostic = async ({ diagnosticId }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to fetch diagnostic");
  return await response.json();
};

export const saveValueMeasures = async ({ id, valueMeasures }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${id}/value-measures/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ valueMeasures }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update value measures");
  }
  return await response.json();
};

export const addCustomCapability = async (workspaceId, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/custom-capabilities/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to create custom capability");
  return await response.json();
};

export const updateCustomCapability = async (
  workspaceId,
  capabilityId,
  data,
) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/custom-capabilities/${capabilityId}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to update custom capability");
  return await response.json();
};

export const deleteCustomCapability = async (workspaceId, capabilityId) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/custom-capabilities/${capabilityId}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to delete custom capability");
  return await response.json();
};

export const getProjectIqNotifications = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/notifications/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch Project IQ notifications",
    );
  }
  return await response.json();
};

export const markProjectIqNotificationSeen = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/notifications/${id}/mark-seen/`,
    {
      method: "POST",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to mark notification as seen");
  }
  return response;
};

export const adminGetProjectIqNotifications = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/notifications/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch Project IQ notifications",
    );
  }
  return await response.json();
};

export const adminCreateProjectIqNotification = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/notifications/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to create Project IQ notification",
    );
  }
  return await response.json();
};

export const adminUpdateProjectIqNotification = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/notifications/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to update Project IQ notification",
    );
  }
  return await response.json();
};

export const adminDeleteProjectIqNotification = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/notifications/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to delete Project IQ notification",
    );
  }
  return response;
};

export const createWorkspaceDiagnostic = async ({
  workspaceId,
  name,
  description = "",
  method,
  questionBankType = null,
  templateId = null,
  background,
  beginningMessage,
  endingMessage,
  headerColor,
  accentColor,
  customBrandingEnabled,
  demographicsQuestions,
  demographicsPlacement,
  demographicsQuestionOverrides,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/diagnostics/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        description,
        method,
        questionBankType,
        templateId,
        background,
        beginningMessage,
        endingMessage,
        headerColor,
        accentColor,
        customBrandingEnabled,
        demographicsQuestions,
        demographicsPlacement,
        demographicsQuestionOverrides,
      }),
    },
  );
  if (response?.error)
    throw new Error(
      response.message || "Failed to create workspace diagnostic",
    );
  return await response.json();
};

export const getSurveyTemplate = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/survey-templates/${id}/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch survey template");
  }
  return await response.json();
};

export const patchDiagnosticChatConversation = async ({
  diagnosticId,
  chatConversationId,
}) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/chat-conversation/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ chatConversationId }),
    },
  );
  if (response?.error)
    throw new Error(
      response.message || "Failed to update diagnostic chat conversation",
    );
  return await response.json();
};

export const patchDiagnosticStatus = async ({ diagnosticId, status }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/status/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to update diagnostic status");
  return await response.json();
};

export const getSurveyTemplates = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/survey-templates/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch survey templates");
  }
  return await response.json();
};

export const getLinks = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/links/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch links");
  }
  return await response.json();
};

export const adminGetSurveyTemplates = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/survey-templates/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch survey templates");
  }
  return await response.json();
};

export const adminGetLinks = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/links/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch links");
  }
  return await response.json();
};

export const adminCreateSurveyTemplate = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/survey-templates/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to create survey template");
  }
  return await response.json();
};

export const adminCreateLink = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/links/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to create link");
  }
  return await response.json();
};

export const adminUpdateSurveyTemplate = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/survey-templates/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update survey template");
  }
  return await response.json();
};

export const adminUpdateLink = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/links/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update link");
  }
  return await response.json();
};

export const adminDeleteSurveyTemplate = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/survey-templates/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete survey template");
  }
  return response;
};

export const adminDeleteLink = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/links/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete link");
  }
  return response;
};

export const getProjectIqContextFileTags = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/context-file-tags/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch context file tags");
  }
  return await response.json();
};

export const adminCreateContextFileTag = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/context-file-tags/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to create tag");
  }
  return await response.json();
};

export const adminUpdateContextFileTag = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/context-file-tags/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update tag");
  }
  return await response.json();
};

export const adminDeleteContextFileTag = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/context-file-tags/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete tag");
  }
  return response;
};

export const registerDiagnosticQuestionBank = async (
  diagnosticId,
  blobName,
) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/question-bank/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ blobName }),
      redirect: "follow",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to register diagnostic question bank");
  }

  return await response.json();
};

export const getWorkspaceAnalytics = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/analytics/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to fetch analytics projects");
  return await response.json();
};

export const getWorkspaceAnalyticsForDiagnostic = async ({
  workspaceId,
  diagnosticId,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/analytics/?diagnostic_id=${diagnosticId}`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to fetch analytics projects");
  return await response.json();
};

export const createWorkspaceAnalytic = async ({
  workspaceId,
  name,
  objective,
  clientName,
  workdayCode,
  diagnosticId,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/analytics/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        objective,
        clientName,
        workdayCode,
        diagnosticId,
      }),
    },
  );
  if (response?.error) {
    const error = new Error(
      response.message || "Failed to create analytics project",
    );
    error.status = response.status;
    throw error;
  }
  return await response.json();
};

export const updateWorkspaceAnalytic = async ({
  workspaceId,
  projectId,
  name,
  objective,
  clientName,
  workdayCode,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/analytics/${projectId}/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, objective, clientName, workdayCode }),
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to update analytics project");
  return await response.json();
};

export const getDeliveryMethodConfig = async (method) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/delivery-methods/${method}/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(
      response.message || "Failed to fetch delivery method config",
    );
  return await response.json();
};

export const adminGetDeliveryMethodConfigs = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/delivery-methods/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error)
    throw new Error(
      response.message || "Failed to fetch delivery method configs",
    );
  return await response.json();
};

export const adminCreateDeliveryMethodConfig = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/delivery-methods/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error)
    throw new Error(
      response.message || "Failed to create delivery method config",
    );
  return await response.json();
};

export const adminUpdateDeliveryMethodConfig = async (method, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/delivery-methods/${method}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error)
    throw new Error(
      response.message || "Failed to update delivery method config",
    );
  return await response.json();
};

export const getPortfolios = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/portfolios/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch portfolios");
  }
  return await response.json();
};

export const getPortfolioDashboardWorkspaces = async () => {
  return fetchAllPaginatedItems(
    `${PROJECT_IQ_API_BASE}/leadership/portfolio-workspaces/`,
    "Failed to fetch portfolio dashboard workspaces",
  );
};

export const createPortfolio = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/portfolios/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to create portfolio");
  }
  return await response.json();
};

export const updatePortfolio = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/portfolios/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update portfolio");
  }
  return await response.json();
};

export const deletePortfolio = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/portfolios/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete portfolio");
  }
  return response;
};

export const getPractices = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/practices/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch practices");
  }
  return await response.json();
};

export const getPracticeDashboardWorkspaces = async () => {
  return fetchAllPaginatedItems(
    `${PROJECT_IQ_API_BASE}/leadership/practice-workspaces/`,
    "Failed to fetch practice dashboard workspaces",
  );
};

export const createPractice = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/practices/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to create practice");
  }
  return await response.json();
};

export const updatePractice = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/practices/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update practice");
  }
  return await response.json();
};

export const deletePractice = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/practices/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete practice");
  }
  return response;
};

export const getPortfolioViewerAccess = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/portfolio-viewer-access/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch portfolio viewer access",
    );
  }
  return await response.json();
};

export const createPortfolioViewerAccess = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/portfolio-viewer-access/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to create portfolio viewer access",
    );
  }
  return await response.json();
};

export const updatePortfolioViewerAccess = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/portfolio-viewer-access/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to update portfolio viewer access",
    );
  }
  return await response.json();
};

export const deletePortfolioViewerAccess = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/portfolio-viewer-access/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to delete portfolio viewer access",
    );
  }
  return response;
};

export const getPracticeViewerAccess = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/practice-viewer-access/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch practice viewer access",
    );
  }
  return await response.json();
};

export const createPracticeViewerAccess = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/practice-viewer-access/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to create practice viewer access",
    );
  }
  return await response.json();
};

export const updatePracticeViewerAccess = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/practice-viewer-access/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to update practice viewer access",
    );
  }
  return await response.json();
};

export const deletePracticeViewerAccess = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/practice-viewer-access/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to delete practice viewer access",
    );
  }
  return response;
};

export const getMyPortfolioViewerAccess = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/portfolio-viewer-access/me/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch portfolio viewer access",
    );
  }
  return await response.json();
};

export const getMyPracticeViewerAccess = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/practice-viewer-access/me/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to fetch practice viewer access",
    );
  }
  return await response.json();
};

export const getMyExecutiveAccess = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/executive-access/me/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch executive access");
  }
  return await response.json();
};

export const getExecutiveAccess = async () => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/executive-access/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch executive access");
  }
  return await response.json();
};

export const createExecutiveAccess = async (data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/executive-access/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to create executive access");
  }
  return await response.json();
};

export const updateExecutiveAccess = async (id, data) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/executive-access/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to update executive access");
  }
  return await response.json();
};

export const deleteExecutiveAccess = async (id) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/admin/executive-access/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete executive access");
  }
  return response;
};

export const generateDiagnosticSummary = async (diagnosticId) => {
  return fetch(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/generate-summary/`,
    {
      method: "POST",
      credentials: "include",
    },
  );
};

export const createProjectIqWorkspaceFolder = async ({
  workspaceId,
  folderPath,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/folders/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        folderPath,
      }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to create folder");
  }
};

export const getProjectIqWorkspaceFolders = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/folders/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch workspace folders");
  }
  const data = await response.json();
  return data.folders;
};

export const deleteProjectIqWorkspaceFolder = async ({
  workspaceId,
  folderPath,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/folders/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        folderPath,
      }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to delete folder");
  }
};

export const bulkSetWorkspaceDocumentShareStatus = async ({
  workspaceId,
  filenames,
  isSharedWithClient,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/share/bulk/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filenames,
        isSharedWithClient,
      }),
    },
  );
  if (response?.error) {
    throw new Error(
      response.message || "Failed to bulk update document share status",
    );
  }
  return await response.json();
};

export const bulkSetWorkspaceDocumentType = async ({
  workspaceId,
  filenames,
  documentType,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/type/bulk/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filenames,
        documentType,
      }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to bulk update document type");
  }
  return await response.json();
};

export const bulkDeleteProjectIqWorkspaceDocuments = async ({
  workspaceId,
  filenames,
}) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/documents/bulk/`,
    {
      method: "DELETE",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filenames,
      }),
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to bulk delete documents");
  }
  return await response.json();
};

export const triggerSowAnalysis = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/sow-analysis/`,
    {
      method: "POST",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to trigger SOW analysis");
  }
  return await response.json();
};

export const getSowAnalysis = async ({ workspaceId }) => {
  const response = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/sow-analysis/`,
    {
      method: "GET",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );
  if (response?.error) {
    throw new Error(response.message || "Failed to fetch SOW analysis status");
  }
  return await response.json();
};
